import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { UserCheck, UserX, RefreshCw, Search, Users } from 'lucide-react';
import PanelButton from '../PanelButton';
import UserDropdown from '../UserDropdown';
import StatusDropdown from '../StatusDropdown';
import { fetchAllUsers } from '../../store/slices/userSlice';
import { addNotification } from '../../store/slices/uiSlice';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { colors } from '../../theme/colors';

const SectionTitle = styled.h3`
  margin: 0 0 0.75rem 0;
  color: #e0e0e0;
  font-size: 0.875rem;
  font-weight: 600;
`;

const SectionContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const Explainer = styled.div`
  color: #a0a0a0;
  font-size: 0.7rem;
  margin-bottom: 0.75rem;
  line-height: 1.4;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 0.75rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.625rem 0.75rem 0.625rem 2.5rem;
  background: #3a3a3a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: #e0e0e0;
  font-size: 0.75rem;
  
  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.3);
  }
  
  &::placeholder {
    color: #a0a0a0;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #a0a0a0;
  display: flex;
  align-items: center;
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const SwitchLabel = styled.label`
  font-size: 0.75rem;
  color: #e0e0e0;
  cursor: pointer;
  user-select: none;
`;

const Switch = styled.label`
  position: relative;
  display: inline-block;
  width: 40px;
  height: 20px;
`;

const SwitchInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  
  &:checked + span {
    background-color: #000000;
  }
  
  &:checked + span:before {
    transform: translateX(20px);
  }
`;

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #4a4a4a;
  transition: 0.3s;
  border-radius: 20px;
  
  &:before {
    position: absolute;
    content: "";
    height: 14px;
    width: 14px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }
`;

const AdminContent = ({ 
  statusFilter: propStatusFilter, 
  setStatusFilter: propSetStatusFilter, 
  assigneeFilter: propAssigneeFilter, 
  setAssigneeFilter: propSetAssigneeFilter,
  searchQuery: propSearchQuery,
  setSearchQuery: propSetSearchQuery,
  statusCounts: propStatusCounts,
  assignees: propAssignees,
  showArchived: propShowArchived,
  setShowArchived: propSetShowArchived
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isSuperAdmin, isAdmin, user: authUser } = useAuth();
  const currentUser = authUser; // Use authenticated user from AuthContext
  const allUsers = useSelector(state => state.user?.allUsers);
  
  // Debug logging
  console.log('AdminContent - currentUser:', currentUser);
  console.log('AdminContent - allUsers:', allUsers.map(u => ({ id: u.id, name: u.full_name, role: u.role })));
  
  const [selectedUserId, setSelectedUserId] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState(propStatusFilter || 'all');
  const [assigneeFilter, setAssigneeFilter] = useState(propAssigneeFilter || 'all');
  const [searchQuery, setSearchQuery] = useState(propSearchQuery || '');
  const [statusCounts, setStatusCounts] = useState(propStatusCounts || {});
  const [assignees, setAssignees] = useState(propAssignees || []);
  const [showArchived, setShowArchived] = useState(propShowArchived || false);

  // Get selected loans from main table via global state
  const getSelectedLoans = () => {
    return window.adminSelectedLoans || [];
  };

  const reloadLoans = () => {
    if (window.adminReloadLoans) {
      window.adminReloadLoans();
    }
  };

  // Poll for selection changes and filter state
  useEffect(() => {
    const interval = setInterval(() => {
      const currentSelection = getSelectedLoans();
      setSelectedCount(currentSelection.length);
      
      // Sync filter state from global
      if (window.adminStatusFilter !== statusFilter) {
        setStatusFilter(window.adminStatusFilter);
      }
      if (window.adminAssigneeFilter !== assigneeFilter) {
        setAssigneeFilter(window.adminAssigneeFilter);
      }
      if (window.adminSearchQuery !== searchQuery) {
        setSearchQuery(window.adminSearchQuery);
      }
      if (window.adminStatusCounts) {
        setStatusCounts(window.adminStatusCounts);
      }
      if (window.adminAssignees) {
        setAssignees(window.adminAssignees);
      }
      if (window.adminShowArchived !== undefined && window.adminShowArchived !== showArchived) {
        setShowArchived(window.adminShowArchived);
      }
    }, 200);

    return () => clearInterval(interval);
  }, [statusFilter, assigneeFilter, searchQuery, showArchived]);

  useEffect(() => {
    // Fetch users if not already loaded
    if (allUsers.length === 0) {
      dispatch(fetchAllUsers());
    }
  }, [dispatch, allUsers.length]);

  const handleAssign = async () => {
    const selectedLoans = getSelectedLoans();
    
    if (!selectedUserId || selectedLoans.length === 0) {
      dispatch(addNotification({
        type: 'error',
        message: 'Please select loans from the table and choose a user to assign to',
        duration: 3000
      }));
      return;
    }

    setActionLoading(true);
    try {
      const response = await api.post('/admin/assign-loans', {
        loan_ids: selectedLoans,
        assigned_to_user_id: parseInt(selectedUserId),
        assigned_by_user_id: currentUser?.id || 1
      });

      const data = response.data;
      if (data.success) {
        dispatch(addNotification({
          type: 'success',
          message: `✅ ${data.message}`,
          duration: 3000
        }));
        reloadLoans();
      }
    } catch (error) {
      console.error('Error assigning loans:', error);
      dispatch(addNotification({
        type: 'error',
        message: '❌ Failed to assign loans. Please try again.',
        duration: 3000
      }));
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnassign = async () => {
    const selectedLoans = getSelectedLoans();
    
    if (selectedLoans.length === 0) {
      dispatch(addNotification({
        type: 'error',
        message: 'Please select loans from the table to unassign',
        duration: 3000
      }));
      return;
    }

    setActionLoading(true);
    try {
      const response = await api.post('/admin/unassign-loans', {
        loan_ids: selectedLoans,
        unassigned_by_user_id: currentUser?.id || 1
      });

      const data = response.data;
      if (data.success) {
        dispatch(addNotification({
          type: 'success',
          message: `✅ ${data.message}`,
          duration: 3000
        }));
        reloadLoans();
      }
    } catch (error) {
      console.error('Error unassigning loans:', error);
      dispatch(addNotification({
        type: 'error',
        message: '❌ Failed to unassign loans. Please try again.',
        duration: 3000
      }));
    } finally {
      setActionLoading(false);
    }
  };

  // Filter users to show all users (including super_admin for self-assignment)
  const assignableUsers = allUsers.filter(u => u.role === 'user' || u.role === 'admin' || u.role === 'super_admin');

  return (
    <div>
      {/* Search and Filters */}
      <SectionContainer>
        <SectionTitle>Search & Filter</SectionTitle>
        <SearchContainer>
          <SearchIcon>
            <Search size={14} />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="Loan ID or Borrower..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              if (window.adminSearchQuery !== undefined) {
                window.adminSearchQuery = e.target.value;
                window.adminApplyFilters?.();
              }
            }}
            data-cy="loan-search-input"
          />
        </SearchContainer>
        
        <div style={{ marginBottom: '0.75rem' }}>
          <Explainer style={{ marginBottom: '0.5rem' }}>Status</Explainer>
          <StatusDropdown
            theme="dark"
            value={statusFilter}
            counts={statusCounts}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              if (window.adminStatusFilter !== undefined) {
                window.adminStatusFilter = e.target.value;
                window.adminApplyFilters?.();
              }
            }}
            data-cy="status-dropdown"
          />
        </div>

        {assignees && assignees.length > 0 && (
          <div>
            <Explainer style={{ marginBottom: '0.5rem' }}>Assigned To</Explainer>
            <UserDropdown
              theme="dark"
              users={[
                { id: 0, full_name: 'All Assignees', avatar_color: '#6b7280', role: '' },
                ...assignees.map(name => {
                  const user = allUsers.find(u => u.full_name === name);
                  return user || { id: name, full_name: name, avatar_color: '#6b7280', role: 'user' };
                })
              ]}
              value={assigneeFilter === 'all' ? '0' : (() => {
                const user = allUsers.find(u => u.full_name === assigneeFilter);
                return user ? String(user.id) : '0';
              })()}
              onChange={(e) => {
                if (e.target.value === '0') {
                  setAssigneeFilter('all');
                  if (window.adminAssigneeFilter !== undefined) {
                    window.adminAssigneeFilter = 'all';
                    window.adminApplyFilters?.();
                  }
                } else {
                  const selectedUser = allUsers.find(u => String(u.id) === e.target.value);
                  if (selectedUser) {
                    setAssigneeFilter(selectedUser.full_name);
                    if (window.adminAssigneeFilter !== undefined) {
                      window.adminAssigneeFilter = selectedUser.full_name;
                      window.adminApplyFilters?.();
                    }
                  }
                }
              }}
              placeholder="All Assignees"
            />
          </div>
        )}
        
        <div style={{ marginTop: '1rem' }}>
          <SwitchContainer>
            <SwitchLabel htmlFor="include-archived-switch">Include Archived</SwitchLabel>
            <Switch>
              <SwitchInput
                id="include-archived-switch"
                type="checkbox"
                checked={showArchived}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  setShowArchived(newValue);
                  if (propSetShowArchived) {
                    propSetShowArchived(newValue);
                  }
                }}
              />
              <Slider />
            </Switch>
          </SwitchContainer>
        </div>
      </SectionContainer>


      <SectionContainer>
        <SectionTitle>Assign To User</SectionTitle>
        <UserDropdown
          users={assignableUsers}
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
          disabled={selectedCount === 0}
          theme="dark"
          placeholder="Select a user..."
          currentUser={currentUser}
          data-cy="assign-to-user-dropdown"
        />
      </SectionContainer>

      <SectionContainer>
        <SectionTitle>Actions</SectionTitle>
        <ButtonGroup>
          <PanelButton
            onClick={handleAssign}
            disabled={selectedCount === 0 || !selectedUserId || actionLoading}
            style={{ flex: 1 }}
            data-cy="assign-button"
          >
            <UserCheck size={16} />
            {actionLoading ? 'Processing...' : 'Assign'}
          </PanelButton>
          <PanelButton
            onClick={handleUnassign}
            disabled={selectedCount === 0 || actionLoading}
            style={{ flex: 1 }}
          >
            <UserX size={16} />
            {actionLoading ? 'Processing...' : 'Unassign'}
          </PanelButton>
        </ButtonGroup>
        <div style={{ marginTop: '0.5rem' }}>
          <PanelButton onClick={reloadLoans}>
            <RefreshCw size={16} />
            Refresh
          </PanelButton>
        </div>
      </SectionContainer>

      {/* User Management Link - at bottom, styled like Admin button */}
      {(isSuperAdmin || isAdmin) && (
        <SectionContainer>
          <PanelButton 
            onClick={() => navigate('/users')}
            style={{ 
              width: '100%',
              background: colors.primary,
              border: 'none',
              fontWeight: '600'
            }}
          >
            <Users size={16} />
            User Management
          </PanelButton>
        </SectionContainer>
      )}
    </div>
  );
};

export default AdminContent;
